import { Checkbox, Group, Loader, NumberInput, Select, SimpleGrid, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import RepeatCreationLabel from 'components/forms/RepeatCreationLabel';
import { EvidenceSection } from 'components/forms/evidence-field/use-evidence-sections';
import OverlayForm from 'components/modals/OverlayForm';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import Toast from 'components/Toast';
import useLocalStorage from 'hooks/use-local-storage';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DASHBOARD_PAGE_PATH,
  EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH,
  EVIDENCE_FIELD_SECTIONS_PAGE_PATH,
} from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

interface SectionFormPageProps {
  edit?: boolean;
  embedded?: boolean;
  sectionIdOverride?: string;
  onSaved?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

interface SectionValues {
  module: string;
  name: string;
  order: number | '';
}

const initialValues: SectionValues = { module: 'devices', name: '', order: 0 };

/** Guards section create/edit routes and their embedded equivalents before loading data. */
export default function EvidenceFieldSectionFormPage(props: SectionFormPageProps) {
  const { roleId } = useApi();
  if (roleId !== ROLE_ADMIN_ID) return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  return <SectionPageContent {...props} />;
}

/** Loads an authoritative edit record, including direct URL and new-tab navigation. */
function SectionPageContent(props: SectionFormPageProps) {
  const { edit = false, embedded = false, sectionIdOverride } = props;
  const { sectionId: routeSectionId } = useParams();
  const sectionId = sectionIdOverride ?? routeSectionId;
  const { getAction } = useApi();
  const [result, setResult] = useState<{ id: string | undefined; section?: EvidenceSection; error?: string }>();
  useEffect(() => {
    if (!edit) return undefined;
    let current = true;
    getAction('DeviceListEvidenceFieldSections')({})
      .then((sections) => {
        if (!current) return;
        const section = sections.find((item) => String(item.sectionId) === sectionId);
        setResult({ id: sectionId, section, error: section ? undefined : 'Sekcia neexistuje.' });
      })
      .catch(() => {
        if (current) setResult({ id: sectionId, error: 'Sekciu sa nepodarilo načítať. Obnovte stránku.' });
      });
    return () => {
      current = false;
    };
  }, [edit, sectionId, getAction]);
  const ready = !edit || Boolean(result && result.id === sectionId);
  const title = edit ? 'Úprava sekcie' : 'Pridanie sekcie';
  const content = !ready ? (
    <Group position="center" py={80} role="status" aria-label="Načítavanie sekcie">
      <Loader />
    </Group>
  ) : result?.error && edit ? (
    <Toast type="fail" message={result.error} withCloseButton={false} />
  ) : (
    <SectionEditor key={edit ? sectionId : 'create'} {...props} section={edit ? result?.section : undefined} />
  );
  if (embedded) return content;
  return (
    <DashboardLayout
      title={title}
      breadcrumbs={[{ title: 'Sekcie polí', link: EVIDENCE_FIELD_SECTIONS_PAGE_PATH.original }, { title }]}
    >
      {content}
    </DashboardLayout>
  );
}

/** Uses the same form, repeat-creation locks and save behavior in every presentation. */
function SectionEditor({
  section,
  embedded = false,
  onSaved,
  onComplete,
  onClose,
  onDirtyChange,
  onBusyChange,
}: SectionFormPageProps & { section?: EvidenceSection }) {
  const { getAction } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [repeatPreference, , updateRepeatPreference] = useLocalStorage(
    'objekto.evidence-field-sections.create-another',
    {
      enabled: false,
    }
  );
  const [locks, , updateLocks] = useLocalStorage('objekto.evidence-field-sections.create-another-locks', {
    module: true,
    name: false,
    order: false,
  });
  const form = useForm<SectionValues>({
    initialValues: section ? { module: section.module, name: section.name, order: section.order } : initialValues,
    validate: {
      name: (value) => (value.trim() ? null : 'Zadajte názov sekcie.'),
      module: (value) => (['devices', 'people'].includes(value) ? null : 'Vyberte použitie.'),
      order: (value) =>
        typeof value === 'number' && Number.isInteger(value) && value >= -2147483648 && value <= 2147483647
          ? null
          : 'Zadajte celočíselné poradie.',
    },
  });
  useDirtyFormHandler(form, embedded ? false : undefined);
  const dirty = form.isDirty();
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);

  /** Persists a valid section, then either starts the next entry or completes the current form. */
  async function save(values: SectionValues) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = { name: values.name.trim(), order: Number(values.order) };
      let savedId = section?.sectionId;
      if (section) {
        await getAction('DeviceUpdateEvidenceFieldSection')({
          parameters: { sectionId: String(section.sectionId) },
          payload,
        });
      } else {
        const response = await getAction('DeviceCreateEvidenceFieldSection')({
          payload: { ...payload, module: values.module },
        });
        savedId = response.sectionId;
      }
      form.resetDirty(values);
      notifications.show({
        title: section ? 'Sekcia bola upravená' : 'Sekcia bola vytvorená',
        message: payload.name,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      onSaved?.();
      if (!section && repeatPreference.enabled) {
        const next = {
          module: locks.module ? values.module : initialValues.module,
          name: locks.name ? values.name : initialValues.name,
          order: locks.order ? values.order : initialValues.order,
        };
        form.setValues(next);
        form.resetDirty(next);
        form.clearErrors();
      } else if (embedded) {
        onComplete?.();
      } else if (searchParams.get('presentation') === 'new-tab') {
        navigate(`${EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH.insert({ sectionId: savedId! })}?presentation=new-tab`);
      } else {
        navigate(EVIDENCE_FIELD_SECTIONS_PAGE_PATH.original);
      }
    } catch {
      setError('Sekciu sa nepodarilo uložiť. Skontrolujte údaje a skúste to znova.');
    } finally {
      setBusy(false);
    }
  }

  /** Shows retention controls only for repeated creation. */
  function label(field: keyof SectionValues, text: string) {
    return !section && repeatPreference.enabled ? (
      <RepeatCreationLabel
        label={text}
        required={field !== 'order'}
        locked={locks[field]}
        onToggle={() => updateLocks({ [field]: !locks[field] })}
        withinPortal={!embedded}
      />
    ) : (
      text
    );
  }
  const FormShell = embedded ? OverlayForm : FormWrapper;
  return (
    <FormShell
      onSubmit={form.onSubmit(save)}
      loading={busy}
      primaryButtonText={section ? 'Uložiť' : 'Vytvoriť'}
      primaryIcon={section ? <IconCheck stroke={1.5} /> : <IconPlus stroke={1.5} />}
      onSecondaryClick={embedded ? onClose : () => navigate(EVIDENCE_FIELD_SECTIONS_PAGE_PATH.original)}
      skipSecondaryButtonConfirm={embedded || !dirty}
      footerRightSide={
        !section && (
          <Checkbox
            label="Po vytvorení pridať ďalšiu"
            checked={repeatPreference.enabled}
            disabled={busy}
            onChange={(event) => updateRepeatPreference({ enabled: event.currentTarget.checked })}
          />
        )
      }
    >
      {error && <Toast type="fail" message={error} withCloseButton={false} />}
      <FormInputGroup groupTitle="Základné info">
        <SimpleGrid cols={2} spacing={40}>
          <Select
            label={label('module', 'Použitie')}
            aria-label="Použitie"
            required
            withAsterisk={Boolean(section) || !repeatPreference.enabled}
            size="lg"
            withinPortal={!embedded}
            disabled={busy || Boolean(section)}
            data={[
              { value: 'devices', label: 'Zariadenia' },
              { value: 'people', label: 'Osoby' },
            ]}
            {...form.getInputProps('module')}
          />
          <TextInput
            label={label('name', 'Názov sekcie')}
            aria-label="Názov sekcie"
            required
            withAsterisk={Boolean(section) || !repeatPreference.enabled}
            size="lg"
            disabled={busy || Boolean(section?.builtinKey)}
            {...form.getInputProps('name')}
          />
          <NumberInput
            label={
              <Group spacing={4}>
                {label('order', 'Poradie')}
                <QuestionMarkTooltip label="Vyššie číslo sa zobrazí skôr." />
              </Group>
            }
            aria-label="Poradie"
            size="lg"
            precision={0}
            min={-2147483648}
            max={2147483647}
            disabled={busy}
            {...form.getInputProps('order')}
          />
        </SimpleGrid>
      </FormInputGroup>
    </FormShell>
  );
}
